import { lazy, Suspense, useState } from "react";
import { api } from "./api.js";
import { useLang, t } from "./i18n.jsx";
import LoginScreen from "./views/Login.jsx";
import Shell from "./views/Shell.jsx";

// ────────────────────────────────────────────────────────────────────────
// Code-splitting: fiecare vedere majoră e un chunk lazy separat, încărcat
// doar când e nevoie de el. Login și Shell rămân eager (mai sus) — trebuie
// randate imediat, înainte/imediat după autentificare, ca ecranul să nu
// rămână gol. Restul (listele Clienți/Evaluări, detaliul unei evaluări cu
// cele 10 tab-uri, Ghidul de metodologie) se încarcă la navigare.
const ClientsView = lazy(() =>
  import("./views/ClientsEvaluari.jsx").then((m) => ({ default: m.ClientsView })));
const EvaluariView = lazy(() =>
  import("./views/ClientsEvaluari.jsx").then((m) => ({ default: m.EvaluariView })));
const EvaluareDetail = lazy(() => import("./views/EvaluareDetail.jsx"));
const GhidView = lazy(() => import("./views/Ghid.jsx"));

// Fallback afișat doar în zona de conținut (sub bara de sus, care rămâne
// randată de Shell) cât timp se încarcă un chunk lazy — evită ecranul gol.
function ViewLoading() {
  const { lang } = useLang();
  return <p>{t(lang, "Se încarcă…")}</p>;
}

// ────────────────────────────────────────────────────────────────────────
// App shell / routing (state-based, fără librărie de routing)
// ────────────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(() => api.getStoredUser());
  const [view, setView] = useState({ name: "clients" });
  const [preGhidView, setPreGhidView] = useState(null);

  if (!user) {
    return <LoginScreen onLoggedIn={setUser} />;
  }

  async function logout() {
    await api.logout();
    setUser(null);
    setView({ name: "clients" });
  }

  function openGhid() {
    setPreGhidView(view);
    setView({ name: "ghid" });
  }

  function closeGhid() {
    setView(preGhidView || { name: "clients" });
    setPreGhidView(null);
  }

  return (
    <Shell user={user} onLogout={logout} onHome={() => setView({ name: "clients" })} onGhid={openGhid}>
      <Suspense fallback={<ViewLoading />}>
        {view.name === "ghid" && <GhidView onBack={closeGhid} />}
        {view.name === "clients" && (
          <ClientsView user={user} onOpenClient={(client) => setView({ name: "evaluari", client })} />
        )}
        {view.name === "evaluari" && (
          <EvaluariView
            user={user}
            client={view.client}
            onBack={() => setView({ name: "clients" })}
            onOpenEvaluare={(ev) => setView({ name: "evaluare", client: view.client, evaluareId: ev.id })}
          />
        )}
        {view.name === "evaluare" && (
          <EvaluareDetail
            user={user}
            evaluareId={view.evaluareId}
            onBack={() => setView({ name: "evaluari", client: view.client })}
          />
        )}
      </Suspense>
    </Shell>
  );
}
